import { Lodging, RoomInfoProducts } from "@b2bportal/lodging-api";
import { Person } from "@b2bportal/air-booking-api";
import {
  PaymentOpaqueValue,
  Product,
  ProductOpaqueValue,
  QuoteSuccess,
} from "@b2bportal/purchase-api";
import {
  cartHelpers,
  getHasPriceChanged,
  ParentState,
  PriceChangeType,
} from "@capone/checkout";
import { SetUserSelectedTravelersList } from "@capone/checkout/build/packages/checkout/src/states/Products/Flight/states/PassengerInformation/events";
import {
  CallState,
  ISelectedTrip,
  ITripTerminus,
  TripDetails,
  WithDiscount,
} from "redmond";
import { assign, DoneInvokeEvent } from "xstate";
import {
  InitializeCheckoutStateEvent,
  InitializeCheckoutStateV2Event,
  SetFinalizePackageResponseEvent,
  SetInitialBookTimeEvent,
  SetPlatformEvent,
  SetTripDescriptionEvent,
  SetTripPurposeEvent,
  SetUserPassengersEvent,
} from "../events";
import {
  getOpaquePayments,
  getPackagesShopPricing,
  getQuotedPackageDiscount,
} from "../selectors/common";
import { PackageOpaqueProductValue, PackagesMachineContext } from "../types";

export const setPaymentFulfillParams = assign(
  (ctx: PackagesMachineContext, _event) => {
    const opaquePayments = getOpaquePayments({ context: ctx });

    ctx[ParentState.cartFulfill].fulfillRequestPayments?.push(
      ...opaquePayments
    );

    return ctx;
  }
);

export const initializeCheckoutState = assign(
  (
    ctx: PackagesMachineContext,
    event: InitializeCheckoutStateEvent | InitializeCheckoutStateV2Event
  ) => {
    ctx.flightShop.selectedTrip = event.payload.selectedTrip as ISelectedTrip;
    ctx.flightShop.tripDetails = event.payload.tripDetails as TripDetails;
    ctx.lodgingShop.fromDate = event.payload.hotelFromDate;
    ctx.lodgingShop.untilDate = event.payload.hotelUntilDate;
    ctx.lodgingShop.selectedLodging = event.payload
      .selectedLodging as unknown as Lodging;
    ctx.lodgingShop.selectedRoom = event.payload.selectedRoom as
      | RoomInfoProducts
      | undefined;
    ctx.lodgingShop.selectedRoomRateId = event.payload.selectedRoomRateId;
    // Under v2, this payload field is Redux-sourced and only ever populated by Review's finalize
    // saga, never by Book's own xstate finalize (setFinalizePackageResponse below) — so a
    // re-dispatch after Book's finalize has already resolved would otherwise wipe out the correct
    // value with undefined. `??` falls back to the existing context value instead. v0 is
    // unaffected: its payload is always defined once Review has run, so this is a no-op there.
    ctx.finalizePackageResponse =
      event.payload.finalizePackageResponse ?? ctx.finalizePackageResponse;
    ctx.packagePricing = event.payload.packagePricing;
    ctx.lodgingShop.guests = {
      adults: event.payload.searchedAdultsCount,
      children: [
        ...event.payload.searchedChildren,
        ...event.payload.searchedInfants,
      ],
    };
    ctx.searchedDestination = event.payload.searchedDestination;
    ctx.flightSearch.departureDate =
      event.payload.searchedDepartureDate ?? null;
    ctx.flightSearch.returnDate = event.payload.searchedReturnDate;
    ctx.flightSearch.destination = event.payload
      .searchedDestination as ITripTerminus;
    ctx.flightSearch.origin = (event.payload.searchedOrigin ??
      null) as ITripTerminus | null;
    ctx.flightShop.benchmarkSliceIds = event.payload.benchmarkSliceIds;
    ctx.lodgingShop.selectedLodgingIndex = event.payload.selectedLodgingIndex;
    ctx.selectedAccount = event.payload.selectedAccount;
    ctx.entryPoint = event.payload.entryPoint;
    ctx.discountInfo = event.payload.discountInfo;
    ctx.experiments = event.payload.experiments;
    ctx.opaqueFlightRequest = event.payload.opaqueFlightRequest;
    ctx.opaqueLodgingRequest = event.payload.opaqueLodgingRequest;
    ctx.searchKey = event.payload.searchKey;
    ctx.sessionInfo = event.payload.sessionInfo;
    ctx.corpBusinessAccounts = event.payload.corpBusinessAccounts;
    // TODO: set modal metadata into context
    ctx.modalMetadata = event.payload.modalMetadata;
    ctx.policyTier = event.payload.policyTier;
    ctx.cartFulfill.channel = event.payload.channel;
    ctx.selectedLodgingIsTopSeller = event.payload.selectedLodgingIsTopSeller;
    ctx.selectedLodgingMealPlan = event.payload.selectedLodgingMealPlan;
    ctx.selectedLodgingIsSearched = event.payload.selectedLodgingIsSearched;
    ctx.lodgingSelectedFromMap = event.payload.lodgingSelectedFromMap;
    return ctx;
  }
);

export const addPackageProduct = assign(
  (ctx: PackagesMachineContext, _event: unknown) => {
    const packageProduct: ProductOpaqueValue = {
      type: "Package",
      value: {
        destinationName:
          ctx.searchedDestination?.label ||
          `${ctx[ParentState.lodgingShop].selectedLodging?.lodging.city}, ${
            ctx[ParentState.lodgingShop].selectedLodging?.lodging.country
          }`,
        savings: ctx.packagePricing?.totalPackageSavings?.fiat,
        ...(window.__mclean_env__.POPULATE_PACKAGES_SAVINGS_V2 &&
        (ctx.packagePricing?.totalPackageSavings?.fiat.value ?? 0) > 0
          ? {
              savingsV2: {
                amount: ctx.packagePricing?.totalPackageSavings?.fiat,
                percentage: (ctx.discountInfo as WithDiscount)
                  .savingsPercentage,
                sortExperimentVariantName: (ctx.discountInfo as WithDiscount)
                  .sortingVariant,
                discountExperimentVariantName: (
                  ctx.discountInfo as WithDiscount
                ).savingsVariant,
              },
            }
          : {}),
      },
    };
    const ctxWithPackage = cartHelpers.addQuoteProduct(packageProduct, ctx);

    return ctxWithPackage;
  }
);

export const addPackageDiscount = assign(
  (ctx: PackagesMachineContext, _event: unknown) => {
    const packageSavings = ctx.packagePricing?.totalPackageSavings?.fiat?.value;
    if (packageSavings && packageSavings > 0) {
      const packageDiscount: PaymentOpaqueValue = {
        type: "PackageSyntheticDiscount" as any, // TODO: remove 'any' cast once Payment model is updated with PackageSyntheticDiscount
        value: {},
      };

      return cartHelpers.addUpdatePayment(packageDiscount, ctx);
    }

    return ctx;
  }
);

export const setPlatform = assign(
  (ctx: PackagesMachineContext, event: SetPlatformEvent) => {
    ctx.platform = event.platform;

    return ctx;
  }
);

export const checkForPriceChange = assign(
  (context: PackagesMachineContext, event: DoneInvokeEvent<QuoteSuccess>) => {
    const packagesShopPricing = getPackagesShopPricing({ context });
    const quoteBreakdown = event.data?.quoteBreakdown;

    if (!quoteBreakdown) return context;

    const quotedPackageProduct = quoteBreakdown?.products.find(
      (product) => product.product.type === Product.Package
    );

    const quotedPackageProductValue = quotedPackageProduct?.product.value as
      | PackageOpaqueProductValue
      | undefined;

    const numTravelers =
      context[ParentState.passengerInformation].selectedPassengerIds.length +
      context[ParentState.passengerInformation].selectedLapInfantIds.length;

    const searchTravelerCount =
      (context[ParentState.lodgingShop].guests?.adults || 0) +
      (context[ParentState.lodgingShop].guests?.children.length || 0);

    const selectedTravelerCountDiffersFromSearched =
      numTravelers !== searchTravelerCount;

    const quotedTaxesAndFeesPerTraveler =
      ((quotedPackageProductValue?.totalPackagePricing?.taxesAndFeesTotal?.fiat
        .value ?? 0) +
        (quotedPackageProductValue?.totalPackagePricing?.payLaterTotal?.value ||
          0)) /
      numTravelers;

    const shopPackageSavings = Math.abs(
      packagesShopPricing?.totalPackageSavings?.fiat.value ?? 0
    );
    const shopPerTravelerDiscount = shopPackageSavings / numTravelers;

    const predictedTotal =
      (packagesShopPricing?.pricePerTraveler?.fiat.value || 0) +
      shopPerTravelerDiscount;
    const priceQuoteTotal =
      (quotedPackageProductValue?.perTravelerPricing?.subtotal?.fiat.value ||
        0) + quotedTaxesAndFeesPerTraveler; // taxes and fees are included in shop per-traveler pricing, so include in quoted value for comparison

    const difference = Math.abs(priceQuoteTotal - predictedTotal);

    const allowedValue = 1;
    const allowedCurrency = "USD";
    const allowedDifference = {
      value: allowedValue,
      currencyCode: allowedCurrency,
    };

    const actualDifference = {
      value: difference,
      currencyCode:
        quotedPackageProductValue?.perTravelerPricing?.subtotal.fiat
          .currencyCode || "USD",
    };

    const hasDifference =
      !selectedTravelerCountDiffersFromSearched && // changing pax updates pricing so don't show price change modal
      getHasPriceChanged(allowedDifference, actualDifference);

    const differenceString = difference.toFixed();
    const priceChange: PriceChangeType = {
      hasDifference,
      predictedTotal,
      priceQuoteTotal,
      ...(hasDifference
        ? {
            difference,
            differenceString,
            isIncrease: priceQuoteTotal > predictedTotal,
          }
        : {}),
    };

    context.cartQuote.priceChange = priceChange;

    return context;
  }
);

export const setRedeemedPackageDiscountAmount = assign(
  (context: PackagesMachineContext) => {
    const quotedPackageDiscount = getQuotedPackageDiscount({ context });
    const packagesShopPricing = getPackagesShopPricing({ context });

    const discountAmount =
      quotedPackageDiscount?.amount.amount ||
      packagesShopPricing?.totalPackageSavings.fiat.value;

    if (discountAmount) {
      context.redeemedPackageDiscountAmount = Math.abs(discountAmount);
    }

    return context;
  }
);

export const setFinalizePackageResponse = assign(
  (
    context: PackagesMachineContext,
    event: DoneInvokeEvent<SetFinalizePackageResponseEvent>
  ) => {
    context.finalizePackageResponse = event.data.finalizePackageResponse;
    context.finalizePackageCallState = CallState.Success;

    // Under v2, Book never goes through `initializeCheckoutState`'s v0-sourced
    // `selectedTrip`/hotel dates — those stay unset. The finalize response
    // (already translated from v2's shape when the flag is on, see
    // `convertFinalizeResponseToFinalizePackageResponse`) carries the real
    // flight/hotel identifiers either way, so backfill from it here rather
    // than requiring every caller to know which version it's running under.
    // Guarded so v0's already-correct `initializeCheckoutState` values are
    // never clobbered.
    const { finalizePackageResponse } = context;
    if (context.flightShop.selectedTrip == null) {
      context.flightShop.selectedTrip = {
        tripId: finalizePackageResponse.flightTripId.value,
        outgoingSliceId: null,
        outgoingFareId: finalizePackageResponse.flightFareId.value,
        returnSliceId: null,
        returnFareId: finalizePackageResponse.flightFareId.value,
      };
    }
    if (context.lodgingShop.fromDate == null) {
      context.lodgingShop.fromDate =
        finalizePackageResponse.lodgingStayDates.from;
    }
    if (context.lodgingShop.untilDate == null) {
      context.lodgingShop.untilDate =
        finalizePackageResponse.lodgingStayDates.until;
    }

    return context;
  }
);

export const setTripPurpose = assign(
  (ctx: PackagesMachineContext, event: SetTripPurposeEvent) => {
    ctx.tripPurpose = event.tripPurpose;

    return ctx;
  }
);

export const setTripDescription = assign(
  (ctx: PackagesMachineContext, event: SetTripDescriptionEvent) => {
    ctx.tripDescription = event.tripDescription;
    ctx.cartFulfill.corporateTripDescription = event.tripDescription;

    return ctx;
  }
);

export const setUserPassengers = assign(
  (ctx: PackagesMachineContext, event: SetUserPassengersEvent) => {
    ctx[ParentState.passengerInformation].userPassengers = event.userPassengers.map(
      (person): Person => ({
        ...person,
        groundLoyalty: person.groundLoyalty ?? {},
      })
    );
    return ctx;
  }
);

export const setUserSelectedTravelersList = assign(
  (ctx: PackagesMachineContext, event: SetUserSelectedTravelersList) => {
    ctx[ParentState.passengerInformation].userSelectedTravelersList =
      event.userSelectedTravelersList;
    return ctx;
  }
);

export const setFinalizePackageCallStateFailed = assign(
  (context: PackagesMachineContext) => {
    context.finalizePackageCallState = CallState.Failed;

    return context;
  }
);

// Marks a finalize call as in flight synchronously, before it resolves —
// see the state machine's INITIALIZE_CHECKOUT_STATE_V2 handler.
export const markFinalizePackageInProcess = assign(
  (context: PackagesMachineContext) => {
    context.finalizePackageCallState = CallState.InProcess;

    return context;
  }
);

export const setInitialBookTime = assign(
  (ctx: PackagesMachineContext, event: SetInitialBookTimeEvent) => {
    ctx.initialTime = event.initialTime;

    return ctx;
  }
);
